import { REPORTS, reportIdFromMenuPath, reportMenuPath, reportTitle } from '../../pages/reports/reportCatalog';

export const SHORTCUT_LIMIT = 8;
export const MENU_COLLAPSED_KEY = 'menuCollapsed';
export const MENU_COLLAPSED_CATEGORIES_KEY = 'menuCollapsedCategories';
export const PIN_LIMIT_MESSAGE = 'Shortcuts hold 8 pages. Unpin one first.';

/** Pages that can be pinned. Dashboard stays fixed at the top of the menu. */
export const PINNABLE_PATHS = [
    'mainboard',
    'loads',
    'tenders',
    'brokers',
    'invoices',
    'statements',
    'trucks',
    'trailers',
    'trailer-management',
    'fuel-cards',
    'inspections',
    'drivers',
    'hiring',
    'reports',
    'employees',
    'teams',
];

const PINNABLE = new Set([
    ...PINNABLE_PATHS,
    ...REPORTS.map((report) => reportMenuPath(report.id)),
]);

export const MENU_ITEMS = [
    { path: 'dashboard', title: 'Dashboard' },
    { path: 'mainboard', title: 'Mainboard' },
    { path: 'loads', title: 'Loads' },
    { path: 'tenders', title: 'Tenders' },
    { path: 'brokers', title: 'Brokers' },
    { path: 'invoices', title: 'Invoices' },
    { path: 'statements', title: 'Statements' },
    { path: 'trucks', title: 'Trucks' },
    { path: 'trailer-management', title: 'Trailer Management' },
    { path: 'trailers', title: 'Trailers' },
    { path: 'fuel-cards', title: 'Fuel Cards' },
    { path: 'inspections', title: 'Inspections' },
    { path: 'drivers', title: 'Drivers' },
    { path: 'hiring', title: 'Onboarding' },
    { path: 'reports', title: 'Reports' },
    { path: 'employees', title: 'Employees' },
    { path: 'teams', title: 'Teams' },
];

const TITLE_BY_PATH = Object.fromEntries(MENU_ITEMS.map((item) => [item.path, item.title]));

export const MENU_GROUPS = [
    { id: 'dispatching', label: 'Dispatching', paths: ['mainboard', 'loads', 'tenders', 'brokers'] },
    { id: 'accounting', label: 'Accounting', paths: ['invoices', 'statements'] },
    { id: 'fleet', label: 'Fleet', paths: ['trucks', 'trailers', 'trailer-management', 'fuel-cards'] },
    { id: 'safety', label: 'Safety', paths: ['inspections', 'drivers', 'hiring'] },
    { id: 'admin', label: 'Admin', paths: ['employees', 'teams'] },
    { id: 'reports', label: null, paths: ['reports'] },
];

const ACCESS = {
    tenders: (can) => can('TENDERS', 'VIEW'),
    brokers: (can) => can('BROKERS', 'VIEW'),
    invoices: (can) => can('INVOICES', 'VIEW'),
    statements: (can) => can('STATEMENTS', 'VIEW'),
    trucks: (can) => can('TRUCKS', 'VIEW'),
    trailers: (can) => can('TRAILERS', 'VIEW'),
    'trailer-management': (can) => can('TRAILER_MANAGEMENT', 'FLAG'),
    'fuel-cards': (can) => can('FUEL_CARDS', 'VIEW'),
    inspections: (can) => can('INSPECTIONS', 'VIEW'),
    drivers: (can) => can('DRIVERS', 'VIEW'),
    hiring: (can) => can('HIRING', 'VIEW'),
    employees: (can) => can('USERS', 'VIEW') || can('ROLES', 'VIEW'),
    teams: (can) => can('TEAMS', 'VIEW'),
};

export const menuTitle = (path) => {
    const reportId = reportIdFromMenuPath(path);
    if (reportId && reportId !== '') return reportTitle(reportId);
    return TITLE_BY_PATH[path] || path;
};

export const canSeePath = (path, can) => {
    const reportId = reportIdFromMenuPath(path);
    if (reportId) {
        const report = REPORTS.find((item) => item.id === reportId);
        return report ? !!report.visible(can) : false;
    }
    if (!TITLE_BY_PATH[path]) return false;
    const rule = ACCESS[path];
    return rule ? !!rule(can) : true;
};

const sameList = (left, right) => (
    left.length === right.length && left.every((value, index) => value === right[index])
);

/** Known pinnable paths only, first occurrence wins, capped at eight. */
export const sanitizePins = (pins) => {
    const next = [];
    for (const path of Array.isArray(pins) ? pins : []) {
        if (typeof path !== 'string' || !PINNABLE.has(path) || next.includes(path)) continue;
        next.push(path);
        if (next.length === SHORTCUT_LIMIT) break;
    }
    return next;
};

/** Pins the user can currently open. A saved pin they can no longer open stays stored but is hidden. */
export const visibleShortcutPins = (pins, canSee) => sanitizePins(pins).filter(canSee);

export const visibleGroups = (canSee) => MENU_GROUPS
    .map((group) => ({
        id: group.id,
        label: group.label,
        items: group.paths.filter(canSee),
    }))
    .filter((group) => group.items.length > 0);

/**
 * Collapsed rail: dashboard, pinned shortcuts, then one entry per category.
 * Category pages stay inside the entry so a hover menu can list them.
 */
export const collapsedRail = (visiblePins, groups) => {
    const rail = [{ kind: 'page', path: 'dashboard', source: 'category' }];
    if (visiblePins.length > 0) {
        rail.push({ kind: 'gap' });
        rail.push({ kind: 'rule' });
        visiblePins.forEach((path) => rail.push({ kind: 'page', path, source: 'shortcut' }));
        rail.push({ kind: 'rule' });
        rail.push({ kind: 'gap', size: 'afterPins' });
    }
    groups.forEach((group, index) => {
        if (index > 0 || visiblePins.length === 0) rail.push({ kind: 'gap' });
        rail.push({
            kind: 'group',
            id: group.id,
            label: group.label || menuTitle(group.items[0]),
            items: group.items,
        });
    });
    return rail;
};

/**
 * Append a pin. Visible pins are capped at five. A page the user can no longer
 * open is kept only while the five saved slots have room.
 */
export const pinPath = (pins, path, canSee) => {
    const current = sanitizePins(pins);
    if (!PINNABLE.has(path) || !canSee(path) || current.includes(path)) {
        return { pins: current, error: null, changed: !sameList(current, Array.isArray(pins) ? pins : []) };
    }
    const visible = current.filter(canSee);
    if (visible.length >= SHORTCUT_LIMIT) {
        return { pins: current, error: PIN_LIMIT_MESSAGE, changed: false };
    }
    const hidden = current.filter((item) => !canSee(item));
    const nextVisible = [...visible, path];
    const roomForHidden = SHORTCUT_LIMIT - nextVisible.length;
    return {
        pins: [...nextVisible, ...hidden.slice(0, roomForHidden)],
        error: null,
        changed: true,
    };
};

export const unpinPath = (pins, path) => sanitizePins(pins).filter((item) => item !== path);
